import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { FormBuilder } from "@angular/forms";

import { distinctUntilChanged, filter } from "rxjs";

import { SlideOverlayPageService } from "@components/shared/overlay/slide-overlay-page/slide-overlay-page.service";
import { RiskAssessmentTemplateResourceType } from "@components/shared/risk-assessment-templates/constants";
import { CommonConstants, TextConstants } from "@shared/constants";
import { ISelectOption } from "@shared/interfaces";
import { LocalizationUtils } from "@shared/utils";

import { RiskAssessmentReportsApiService } from "../../../api";
import {
  riskAssessmentReportOrderedResourceTypes,
  RiskAssessmentReportStatus,
} from "../../../constants";
import { RiskAssessmentReportRecord } from "../../../models";
import { RiskAssessmentReportsRecordService } from "../../../services";

interface ResourceTypeOption extends ISelectOption {
  value: RiskAssessmentTemplateResourceType | null;
}

@Component({
  selector: "app-risk-assessment-report-overlay-reports-panel",
  templateUrl: "./risk-assessment-report-overlay-reports-panel.component.html",
  styleUrls: ["./risk-assessment-report-overlay-reports-panel.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class RiskAssessmentReportOverlayReportsPanelComponent {
  readonly overlay = inject(SlideOverlayPageService);

  private readonly fb = inject(FormBuilder);

  private readonly reportsApi = inject(RiskAssessmentReportsApiService);

  private readonly reportsRecordService = inject(RiskAssessmentReportsRecordService);

  readonly resourceType = input<RiskAssessmentTemplateResourceType>();

  readonly excludeReportIds = input<string[]>([]);

  readonly add = output<RiskAssessmentReportRecord>();

  readonly isLoading = signal(true);

  private readonly reports = signal<RiskAssessmentReportRecord[]>([]);

  readonly availableReports = computed(() =>
    this.reports().filter((report) => !this.excludeReportIds().includes(report.id)),
  );

  readonly form = this.fb.group({
    resourceType: this.fb.control<ResourceTypeOption>(undefined),
  });

  readonly resourceTypeValue = toSignal(
    this.form.controls.resourceType.valueChanges.pipe(filter(Boolean), distinctUntilChanged()),
  );

  readonly translations = {
    closeTp: TextConstants.CLOSE,
    completedDate: TextConstants.DATE_COMPLETED,
    validUntil: TextConstants.VALID_UNTIL,
    completedRiskAssessments: $localize`:@@riskassessmentreportoverlay.references.completedriskassessments:Completed risk assessments`,
  };

  readonly resourceTypeOptions: ResourceTypeOption[] = riskAssessmentReportOrderedResourceTypes.map(
    (status) => ({
      label: LocalizationUtils.getRiskAssessmentTemplateResourceType[status],
      value: status,
    }),
  );

  readonly type = RiskAssessmentTemplateResourceType;

  constructor() {
    effect(() => {
      const option = this.resourceTypeOptions.find(
        (option) =>
          option.value === (this.resourceType() || riskAssessmentReportOrderedResourceTypes[0]),
      );

      this.form.controls.resourceType.setValue(option);
    });

    effect(() => {
      this.availableReports(); // trigger
      this.isLoading.set(false);
    });

    effect(async () => await this.loadReports());
  }

  async loadReports() {
    this.isLoading.set(true);

    const resourceType = this.resourceTypeValue().value;

    const { content } = await this.reportsApi.getAll({
      recordType: resourceType,
      status: RiskAssessmentReportStatus.COMPLETED,
      size: CommonConstants.MAX_API_GET_ITEMS_SIZE,
    });

    const reports = await this.reportsRecordService.get(content, resourceType);

    this.reports.set(reports);
  }

  onAdd(report: RiskAssessmentReportRecord) {
    this.isLoading.set(true);
    this.add.emit(report);
  }
}
